// Traducción de errores del backend a mensajes en español para la UI.
// El servidor responde `message` = código estable y `details` = explicación segura en español
// (ver supabase/migrations/20261012000500_authz.sql). Nunca se muestra texto SQL bruto.

export const SERVER_ERROR_CODES = [
  'permission',
  'not_found',
  'validation',
  'folio_taken',
  'client_taken',
  'version_conflict',
  'invalid_transition',
  'requirements_unmet',
  'order_locked',
  'media_conflict',
  'not_confirmed',
  'object_present',
  'quota',
  'password_change_required',
] as const;

export type ServerErrorCode = (typeof SERVER_ERROR_CODES)[number];
export type ErrorCode = ServerErrorCode | 'network' | 'unknown';

export interface AppError {
  code: ErrorCode;
  /** Mensaje listo para mostrar al usuario. */
  message: string;
  /** Requisitos faltantes (requirements_unmet), ya traducidos. */
  missing?: string[];
  /** Versión vigente de la orden o del cliente (version_conflict). */
  currentVersion?: number;
  /** Cliente que ya tiene esa identificación (client_taken). */
  clientId?: string;
}

const DEFAULT_MESSAGES: Record<ErrorCode, string> = {
  permission: 'No tienes permiso para realizar esta acción o tu cuenta ya no tiene acceso.',
  not_found: 'El registro no existe o ya no está disponible.',
  validation: 'Revisa los datos ingresados.',
  folio_taken: 'Ya existe una orden con esa OS. Verifica que esté bien escrita.',
  client_taken: 'Ya existe un cliente con esa identificación.',
  version_conflict: 'La orden cambió mientras la editabas. Revisa los cambios antes de guardar de nuevo.',
  invalid_transition: 'Ese cambio de estado no está permitido desde el estado actual.',
  requirements_unmet: 'Faltan requisitos para cambiar el estado.',
  order_locked: 'La orden no admite este cambio en su estado actual.',
  media_conflict: 'El archivo no coincide con su registro. Vuelve a seleccionarlo.',
  not_confirmed: 'El archivo todavía no llegó al servidor. Reintenta la carga.',
  object_present: 'La eliminación del archivo aún no termina. Se reintentará automáticamente.',
  quota: 'Se alcanzó la capacidad de almacenamiento. Avisa al administrador.',
  password_change_required: 'Cambia tu contraseña temporal para continuar.',
  network: 'No hay conexión con el servidor. Revisa tu internet e inténtalo de nuevo.',
  unknown: 'Ocurrió un error inesperado. Inténtalo de nuevo.',
};

// Códigos cuyo `details` del servidor es más útil que el mensaje genérico.
const USE_SERVER_DETAIL: ReadonlySet<ErrorCode> = new Set(['validation', 'order_locked', 'permission', 'client_taken']);

export const REQUIREMENT_LABELS: Record<string, string> = {
  technician: 'Asignar un técnico',
  model: 'Modelo del equipo',
  serial: 'Número de serie',
  diagnosis: 'Descripción Problema',
  solution: 'Descripción Proceso Realizado',
  media_ready: 'Evidencias adjuntas',
  media_pending: 'Sin cargas ni eliminaciones pendientes',
};

interface ErrorLike {
  code?: unknown;
  message?: unknown;
  details?: unknown;
  hint?: unknown;
  name?: unknown;
}

function isServerCode(value: unknown): value is ServerErrorCode {
  return typeof value === 'string' && (SERVER_ERROR_CODES as readonly string[]).includes(value);
}

function parseHint(hint: unknown): unknown {
  if (typeof hint !== 'string' || hint === '') return undefined;
  try {
    return JSON.parse(hint) as unknown;
  } catch {
    return undefined;
  }
}

function fromSqlState(sqlState: string): ErrorCode {
  if (sqlState === '42501') return 'permission';
  if (sqlState === 'PT404') return 'not_found';
  if (sqlState === 'PT507') return 'quota';
  if (sqlState === 'PT428') return 'password_change_required';
  if (['PT400', '23514', '23502', '22P02', '22007', '22008', '23503'].includes(sqlState)) return 'validation';
  return 'unknown';
}

/** Error lanzado por la capa de datos con un AppError ya traducido. */
export class AppFailure extends Error {
  readonly appError: AppError;

  constructor(appError: AppError) {
    super(appError.message);
    this.name = 'AppFailure';
    this.appError = appError;
  }
}

/** Respuesta de error de una Edge Function: { code, message } con mensaje seguro en español. */
export function fromFunctionBody(body: unknown): AppError {
  const b = (body ?? {}) as { code?: unknown; message?: unknown };
  const code: ErrorCode = isServerCode(b.code) ? b.code : 'unknown';
  const message = typeof b.message === 'string' && b.message.trim() !== '' ? b.message : DEFAULT_MESSAGES[code];
  return { code, message: code === 'unknown' ? DEFAULT_MESSAGES.unknown : message };
}

export function toAppError(error: unknown): AppError {
  if (error instanceof AppFailure) return error.appError;
  if (error instanceof TypeError || (error as ErrorLike | null)?.name === 'AbortError') {
    return { code: 'network', message: DEFAULT_MESSAGES.network };
  }
  const e = (error ?? {}) as ErrorLike;
  // supabase-js convierte los fallos de fetch en un objeto con el texto del TypeError.
  const text = `${typeof e.message === 'string' ? e.message : ''} ${typeof e.details === 'string' ? e.details : ''}`;
  if (
    !isServerCode(e.message) &&
    /failed to fetch|networkerror|load failed|fetch failed|network request failed/i.test(text)
  ) {
    return { code: 'network', message: DEFAULT_MESSAGES.network };
  }
  const code: ErrorCode = isServerCode(e.message)
    ? e.message
    : typeof e.code === 'string'
      ? fromSqlState(e.code)
      : 'unknown';

  const detail = typeof e.details === 'string' && e.details.trim() !== '' ? e.details.trim() : undefined;
  const result: AppError = {
    code,
    message: isServerCode(e.message) && USE_SERVER_DETAIL.has(code) && detail ? detail : DEFAULT_MESSAGES[code],
  };

  const hint = parseHint(e.hint);
  if (code === 'requirements_unmet' && Array.isArray(hint)) {
    result.missing = hint.filter((h): h is string => typeof h === 'string').map((h) => REQUIREMENT_LABELS[h] ?? h);
  }
  if (code === 'version_conflict' && hint && typeof hint === 'object' && 'current_version' in hint) {
    const v = (hint as { current_version: unknown }).current_version;
    if (typeof v === 'number') result.currentVersion = v;
  }
  if (code === 'client_taken' && hint && typeof hint === 'object' && 'client_id' in hint) {
    const id = (hint as { client_id: unknown }).client_id;
    if (typeof id === 'string') result.clientId = id;
  }
  return result;
}
